//frontend/src/pages/gro/RiskInventoryItemActionPlansPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Link2,
  Pencil,
  Plus,
  Trash2,
  Unlink2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateInventoryItemActionPlanItem,
  useInventoryActionPlanResponsibleUsers,
  useInventoryItemActionPlanDetail,
  useInventoryItemActionPlans,
  useLinkActionPlanModelToInventoryItem,
  useRiskInventoryDetail,
  useSearchActionPlanModels,
  useSuggestedActionPlanModels,
  useDeleteInventoryItemActionPlanItem,
  useSetInventoryItemActionPlanItemStatus,
  useUnlinkInventoryItemActionPlan,
  useUpdateInventoryItemActionPlanItem,
} from "@/hooks/useRiskInventories";
import type {
  OperationalPlanItemStatus,
  OperationalPlanPriority,
  RiskInventoryItem,
  RiskInventoryOperationalActionPlan,
  RiskInventoryOperationalActionPlanItem,
  RiskInventoryOperationalActionPlanItemCreateIn,
} from "@/types/riskInventory";

const EMPTY_ACTION_FORM: RiskInventoryOperationalActionPlanItemCreateIn = {
  title: "",
  description: "",
  priority: "medium",
  sort_order: 0,
  expected_result: "",
  status: "not_started",
  responsible_user_id: null,
  start_date: null,
  due_date: null,
};

function riskBadgeClass(level?: string | null) {
  const normalized = (level || "").toLowerCase();
  if (normalized.includes("crít")) return "border-red-200 bg-red-50 text-red-700";
  if (normalized.includes("alto")) return "border-amber-200 bg-amber-50 text-amber-700";
  if (normalized.includes("moder")) return "border-slate-200 bg-slate-100 text-slate-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function itemStatusBadgeClass(status?: string | null) {
  if (status === "closed") return "border-red-200 bg-red-50 text-red-700";
  if (status === "mitigated") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function itemStatusLabel(status?: string | null) {
  if (status === "closed") return "Encerrado";
  if (status === "mitigated") return "Mitigado";
  return "Aberto";
}

function planStatusLabel(status: string) {
  if (status === "in_progress") return "Em andamento";
  if (status === "completed") return "Concluído";
  if (status === "cancelled") return "Cancelado";
  return "Não iniciado";
}

function planStatusBadgeClass(status: string) {
  if (status === "in_progress") return "border-blue-200 bg-blue-50 text-blue-700";
  if (status === "completed") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "cancelled") return "border-red-200 bg-red-50 text-red-700";
  return "border-slate-200 bg-slate-100 text-slate-700";
}

function actionStatusLabel(status: OperationalPlanItemStatus) {
  if (status === "in_progress") return "Em andamento";
  if (status === "completed") return "Concluído";
  if (status === "cancelled") return "Cancelado";
  return "Não iniciado";
}

function priorityLabel(priority: OperationalPlanPriority) {
  if (priority === "critical") return "Crítica";
  if (priority === "high") return "Alta";
  if (priority === "low") return "Baixa";
  return "Média";
}

function formatDateBR(value?: string | null) {
  if (!value) return "-";
  const [year, month, day] = value.slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold leading-none">{value}</div>
      </CardContent>
    </Card>
  );
}

type ActionFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: RiskInventoryOperationalActionPlanItemCreateIn) => Promise<void>;
  loading?: boolean;
  responsibleUsers: { id: string; name: string }[];
  initialValue?: RiskInventoryOperationalActionPlanItem | null;
  nextSortOrder?: number;
};

function ActionFormDialog({
  open,
  onOpenChange,
  onSubmit,
  loading = false,
  responsibleUsers,
  initialValue,
  nextSortOrder = 0,
}: ActionFormDialogProps) {
  const [form, setForm] = useState<RiskInventoryOperationalActionPlanItemCreateIn>(EMPTY_ACTION_FORM);

  useEffect(() => {
    if (open) {
      if (initialValue) {
        setForm({
          title: initialValue.title || "",
          description: initialValue.description || "",
          priority: initialValue.priority,
          sort_order: initialValue.sort_order,
          expected_result: initialValue.expected_result || "",
          status: initialValue.status,
          responsible_user_id: initialValue.responsible_user_id || null,
          start_date: initialValue.start_date || null,
          due_date: initialValue.due_date || null,
        });
      } else {
        setForm({ ...EMPTY_ACTION_FORM, sort_order: nextSortOrder });
      }
    }
  }, [open, initialValue, nextSortOrder]);

  async function handleSubmit() {
    if (!form.title.trim()) {
      toast.error("Informe o título da ação.");
      return;
    }
    await onSubmit({
      ...form,
      title: form.title.trim(),
      description: form.description?.trim() || null,
      expected_result: form.expected_result?.trim() || null,
      responsible_user_id: form.responsible_user_id || null,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initialValue ? "Editar ação" : "Nova ação"}</DialogTitle>
          <DialogDescription>
            Esta ação será vinculada apenas ao plano operacional do item do inventário.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Título</Label>
            <Input value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} />
          </div>

          <div className="grid gap-2">
            <Label>Descrição</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} rows={4} />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="grid gap-2">
              <Label>Prioridade</Label>
              <Select value={form.priority} onValueChange={(value) => setForm((prev) => ({ ...prev, priority: value as OperationalPlanPriority }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value as OperationalPlanItemStatus }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Não iniciado</SelectItem>
                  <SelectItem value="in_progress">Em andamento</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Ordem</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="grid gap-2 md:col-span-1">
              <Label>Responsável</Label>
              <Select
                value={form.responsible_user_id || "none"}
                onValueChange={(value) => setForm((prev) => ({ ...prev, responsible_user_id: value === "none" ? null : value }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem responsável</SelectItem>
                  {responsibleUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Início</Label>
              <Input type="date" value={form.start_date || ""} onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value || null }))} />
            </div>
            <div className="grid gap-2">
              <Label>Prazo</Label>
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm((prev) => ({ ...prev, due_date: e.target.value || null }))} />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Resultado esperado</Label>
            <Textarea value={form.expected_result || ""} onChange={(e) => setForm((prev) => ({ ...prev, expected_result: e.target.value }))} rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={loading}>{initialValue ? "Salvar" : "Adicionar ação"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type OperationalPlanCardProps = {
  itemId: string;
  plan: RiskInventoryOperationalActionPlan;
  responsibleUsers: { id: string; name: string }[];
};

function OperationalPlanCard({ itemId, plan, responsibleUsers }: OperationalPlanCardProps) {
  const [open, setOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<RiskInventoryOperationalActionPlanItem | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState<RiskInventoryOperationalActionPlanItem | null>(null);

  const detailQ = useInventoryItemActionPlanDetail(itemId, plan.id, open);
  const createItemMut = useCreateInventoryItemActionPlanItem();
  const updateItemMut = useUpdateInventoryItemActionPlanItem();
  const setStatusMut = useSetInventoryItemActionPlanItemStatus();
  const deleteItemMut = useDeleteInventoryItemActionPlanItem();
  const unlinkMut = useUnlinkInventoryItemActionPlan();

  const detail = detailQ.data;
  const items = detail?.items ?? [];

  async function handleSaveAction(payload: RiskInventoryOperationalActionPlanItemCreateIn) {
    try {
      if (editingAction) {
        await updateItemMut.mutateAsync({ itemId, planId: plan.id, planItemId: editingAction.id, body: payload });
        toast.success("Ação atualizada com sucesso.");
      } else {
        await createItemMut.mutateAsync({ itemId, planId: plan.id, body: payload });
        toast.success("Ação adicionada com sucesso.");
      }
      setActionDialogOpen(false);
      setEditingAction(null);
    } catch {
      toast.error("Não foi possível salvar a ação.");
    }
  }

  async function handleDeleteAction() {
    if (!deleteAction) return;
    try {
      await deleteItemMut.mutateAsync({ itemId, planId: plan.id, planItemId: deleteAction.id });
      toast.success("Ação removida com sucesso.");
      setDeleteAction(null);
    } catch {
      toast.error("Não foi possível remover a ação.");
    }
  }

  async function handleUnlinkPlan() {
    try {
      await unlinkMut.mutateAsync({ itemId, planId: plan.id });
      toast.success("Plano desvinculado com sucesso.");
      setUnlinkOpen(false);
    } catch {
      toast.error("Não foi possível desvincular o plano.");
    }
  }

  async function handleStatusChange(planItemId: string, status: OperationalPlanItemStatus) {
    try {
      await setStatusMut.mutateAsync({ itemId, planId: plan.id, planItemId, status });
      toast.success("Status da ação atualizado.");
    } catch {
      toast.error("Não foi possível atualizar o status da ação.");
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base">{plan.title}</CardTitle>
                <Badge variant="outline" className={planStatusBadgeClass(plan.status)}>{planStatusLabel(plan.status)}</Badge>
                <Badge variant="outline" className="border-slate-200 bg-slate-100 text-slate-700">
                  {plan.link_origin === "suggested" ? "Sugerido" : "Manual"}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span>{plan.items_count} ações</span>
                <span>{plan.active_items_count} ativas</span>
                <span>NI: {plan.not_started_count}</span>
                <span>EA: {plan.in_progress_count}</span>
                <span>CO: {plan.completed_count}</span>
                <span>CA: {plan.cancelled_count}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setActionDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Nova ação
              </Button>
              <Button variant="outline" onClick={() => setUnlinkOpen(true)}>
                <Unlink2 className="mr-2 h-4 w-4" /> Desvincular
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpen((prev) => !prev)}>
                {open ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                {open ? "Ocultar ações" : "Ver ações"}
              </Button>
            </div>
          </div>
        </CardHeader>

        <Collapsible open={open}>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {detailQ.isLoading ? (
                <div className="py-6 text-sm text-muted-foreground">Carregando ações...</div>
              ) : items.length === 0 ? (
                <EmptyState
                  title="Nenhuma ação neste plano"
                  description="Adicione a primeira ação operacional para este plano vinculado ao item."
                  actionLabel="Nova ação"
                  onAction={() => setActionDialogOpen(true)}
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Prioridade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Responsável</TableHead>
                      <TableHead>Início</TableHead>
                      <TableHead>Prazo</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-medium">{item.title}</div>
                          {item.expected_result ? <div className="text-xs text-muted-foreground">{item.expected_result}</div> : null}
                        </TableCell>
                        <TableCell>{item.item_origin === "template" ? "Template" : "Manual"}</TableCell>
                        <TableCell>{priorityLabel(item.priority)}</TableCell>
                        <TableCell>
                          <Select value={item.status} onValueChange={(value) => handleStatusChange(item.id, value as OperationalPlanItemStatus)}>
                            <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_started">Não iniciado</SelectItem>
                              <SelectItem value="in_progress">Em andamento</SelectItem>
                              <SelectItem value="completed">Concluído</SelectItem>
                              <SelectItem value="cancelled">Cancelado</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>{item.responsible_user_name || "-"}</TableCell>
                        <TableCell>{formatDateBR(item.start_date)}</TableCell>
                        <TableCell>{formatDateBR(item.due_date)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingAction(item);
                                setActionDialogOpen(true);
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" /> Editar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setDeleteAction(item)}>
                              <Trash2 className="mr-2 h-4 w-4" /> Remover
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <ActionFormDialog
        open={actionDialogOpen}
        onOpenChange={(value) => {
          setActionDialogOpen(value);
          if (!value) setEditingAction(null);
        }}
        initialValue={editingAction}
        nextSortOrder={items.length}
        responsibleUsers={responsibleUsers}
        loading={createItemMut.isPending || updateItemMut.isPending}
        onSubmit={handleSaveAction}
      />

      <ConfirmDialog
        open={unlinkOpen}
        onOpenChange={setUnlinkOpen}
        title="Desvincular plano"
        description="Deseja realmente desvincular este plano do item do inventário?"
        onConfirm={handleUnlinkPlan}
        confirmLabel="Desvincular"
      />

      <ConfirmDialog
        open={!!deleteAction}
        onOpenChange={(open) => !open && setDeleteAction(null)}
        title="Remover ação"
        description="Deseja realmente remover esta ação do plano operacional do item?"
        onConfirm={handleDeleteAction}
        confirmLabel="Remover"
      />
    </>
  );
}

export default function RiskInventoryItemActionPlansPage() {
  const navigate = useNavigate();
  const { inventoryId, itemId } = useParams<{ inventoryId: string; itemId: string }>();
  const [search, setSearch] = useState("");
  const [suggestedSearch, setSuggestedSearch] = useState("");
  const [suggestedSelected, setSuggestedSelected] = useState<Record<string, boolean>>({});
  const [manualSelected, setManualSelected] = useState<Record<string, boolean>>({});

  const detailQ = useRiskInventoryDetail(inventoryId);
  const suggestedQ = useSuggestedActionPlanModels(itemId);
  const manualQ = useSearchActionPlanModels(itemId, search, true);
  const plansQ = useInventoryItemActionPlans(itemId);
  const responsibleUsersQ = useInventoryActionPlanResponsibleUsers(inventoryId);
  const linkMut = useLinkActionPlanModelToInventoryItem();

  const inventory = detailQ.data;
  const item = useMemo<RiskInventoryItem | null>(() => {
    const items = inventory?.items ?? [];
    return items.find((entry) => entry.id === itemId) || null;
  }, [inventory, itemId]);

  const suggested = suggestedQ.data ?? [];
  const manual = useMemo(
    () => (manualQ.data ?? []).filter((entry) => !entry.linked_to_risk),
    [manualQ.data],
  );
  const linkedPlans = plansQ.data ?? [];
  const responsibleUsers = responsibleUsersQ.data ?? [];

  const linkedModelIds = useMemo(
    () => new Set(linkedPlans.map((plan) => plan.action_plan_model_id).filter(Boolean)),
    [linkedPlans],
  );

  const suggestedAvailable = useMemo(
    () => suggested.filter((entry) => !linkedModelIds.has(entry.action_plan_model_id)),
    [suggested, linkedModelIds],
  );

  const manualAvailable = useMemo(
    () => manual.filter((entry) => !linkedModelIds.has(entry.action_plan_model_id)),
    [manual, linkedModelIds],
  );

  const suggestedFiltered = useMemo(() => {
    const term = suggestedSearch.trim().toLowerCase();
    if (!term) return suggestedAvailable;
    return suggestedAvailable.filter((entry) => {
      const haystack = [entry.title, entry.code, entry.action_plan_model_id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [suggestedAvailable, suggestedSearch]);

  const manualFiltered = useMemo(() => manualAvailable, [manualAvailable]);

  const selectedSuggestedIds = useMemo(() => Object.entries(suggestedSelected).filter(([, checked]) => checked).map(([id]) => id), [suggestedSelected]);
  const selectedManualIds = useMemo(() => Object.entries(manualSelected).filter(([, checked]) => checked).map(([id]) => id), [manualSelected]);

  const summary = useMemo(() => {
    return linkedPlans.reduce(
      (acc, plan) => {
        acc.plans += 1;
        acc.active += plan.active_items_count || 0;
        acc.notStarted += plan.not_started_count || 0;
        acc.inProgress += plan.in_progress_count || 0;
        acc.completed += plan.completed_count || 0;
        acc.cancelled += plan.cancelled_count || 0;
        return acc;
      },
      { plans: 0, active: 0, notStarted: 0, inProgress: 0, completed: 0, cancelled: 0 },
    );
  }, [linkedPlans]);

  async function handleLink(ids: string[], linkOrigin: "suggested" | "manual") {
    if (!itemId || ids.length === 0) return;
    try {
      await Promise.all(
        ids.map((actionPlanModelId) => linkMut.mutateAsync({ itemId, body: { action_plan_model_id: actionPlanModelId, link_origin: linkOrigin } })),
      );
      toast.success(ids.length === 1 ? "Plano vinculado com sucesso." : "Planos vinculados com sucesso.");
      if (linkOrigin === "suggested") setSuggestedSelected({});
      if (linkOrigin === "manual") setManualSelected({});
    } catch {
      toast.error("Não foi possível vincular os planos selecionados.");
    }
  }

  function toggleAllSuggested(checked: boolean) {
    const next: Record<string, boolean> = {};
    suggestedFiltered.forEach((entry) => {
      next[entry.action_plan_model_id] = checked;
    });
    setSuggestedSelected(next);
  }

  if (!inventory || !item) return null;

  const allSuggestedSelected = suggestedFiltered.length > 0 && suggestedFiltered.every((entry) => suggestedSelected[entry.action_plan_model_id]);

  return (
    <div className="space-y-6">
      <PageHeader title="Planos de Ação do Item" description="Gestão operacional dos planos e ações vinculadas a este item do inventário.">
        <Button variant="outline" onClick={() => navigate(`/gro/risk-inventories/${inventoryId}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="px-4 py-3">
          <div className="grid gap-3 xl:grid-cols-[minmax(0,2.4fr)_minmax(160px,1fr)_minmax(180px,1fr)_minmax(200px,1fr)] xl:items-center">
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Item do inventário</div>
              <div className="mt-1 flex items-center gap-2 min-w-0">
                <div className="truncate font-semibold">{item.title}</div>
                <Badge variant="outline" className={itemStatusBadgeClass(item.status)}>{itemStatusLabel(item.status)}</Badge>
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Classificação do risco</div>
              <div className="mt-1"><Badge variant="outline" className={riskBadgeClass(item.risk_level)}>{item.risk_level || "-"}</Badge></div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Inventário</div>
              <div className="mt-1 truncate font-medium">{inventory.name}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Campanha / Origem</div>
              <div className="mt-1 truncate font-medium">{item.risk_source_type === "normex_copilot" ? "Normex Copilot" : item.campaign_name || (item.risk_source_type === "manual" ? "Manual" : "Campanha")}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-2 md:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Planos vinculados" value={summary.plans} />
        <StatCard label="Ações ativas" value={summary.active} />
        <StatCard label="Não iniciadas" value={summary.notStarted} />
        <StatCard label="Em andamento" value={summary.inProgress} />
        <StatCard label="Concluídas" value={summary.completed} />
        <StatCard label="Canceladas" value={summary.cancelled} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Planos sugeridos para este risco</CardTitle>
                <div className="text-sm text-muted-foreground">Selecione um ou mais planos sugeridos e vincule ao item.</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => toggleAllSuggested(!allSuggestedSelected)} disabled={!suggestedFiltered.length}>
                {allSuggestedSelected ? "Desmarcar todos" : "Selecionar todos"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input placeholder="Buscar por título, código ou ID" value={suggestedSearch} onChange={(e) => setSuggestedSearch(e.target.value)} />
            <div className="h-[124px] overflow-y-auto rounded-lg border">
              {!suggestedFiltered.length ? (
                <div className="p-4 text-sm text-muted-foreground">Nenhum plano sugerido para este risco.</div>
              ) : (
                <div className="divide-y">
                  {suggestedFiltered.map((entry) => (
                    <label key={entry.action_plan_model_id} className="flex h-[56px] cursor-pointer items-center gap-3 px-3 py-2">
                      <Checkbox
                        checked={!!suggestedSelected[entry.action_plan_model_id]}
                        onCheckedChange={(checked) =>
                          setSuggestedSelected((prev) => ({ ...prev, [entry.action_plan_model_id]: checked === true }))
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="truncate pr-2 font-medium leading-tight">{entry.title}</div>
                          <Badge variant="secondary" className="shrink-0">{entry.items_count} ações</Badge>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{entry.code}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => handleLink(selectedSuggestedIds, "suggested")} disabled={!selectedSuggestedIds.length || linkMut.isPending}>
                <Link2 className="mr-2 h-4 w-4" /> Vincular selecionados
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Buscar outro plano</CardTitle>
            <div className="text-sm text-muted-foreground">Busque planos avulsos já cadastrados e vincule ao item.</div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input placeholder="Buscar por título, código ou ID" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="h-[124px] overflow-y-auto rounded-lg border">
              {!manualFiltered.length ? (
                <div className="p-4 text-sm text-muted-foreground">Nenhum plano disponível para o filtro informado.</div>
              ) : (
                <div className="divide-y">
                  {manualFiltered.map((entry) => (
                    <label key={entry.action_plan_model_id} className="flex h-[56px] cursor-pointer items-center gap-3 px-3 py-2">
                      <Checkbox
                        checked={!!manualSelected[entry.action_plan_model_id]}
                        onCheckedChange={(checked) =>
                          setManualSelected((prev) => ({ ...prev, [entry.action_plan_model_id]: checked === true }))
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="truncate pr-2 font-medium leading-tight">{entry.title}</div>
                          <Badge variant="secondary" className="shrink-0">{entry.items_count} ações</Badge>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{entry.code}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => handleLink(selectedManualIds, "manual")} disabled={!selectedManualIds.length || linkMut.isPending}>
                <Link2 className="mr-2 h-4 w-4" /> Vincular selecionados
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Planos já vinculados ao item</CardTitle>
          <div className="text-sm text-muted-foreground">Expanda cada plano para visualizar e gerenciar as ações operacionais.</div>
        </CardHeader>
        <CardContent>
          {!linkedPlans.length ? (
            <EmptyState
              title="Nenhum plano vinculado"
              description="Vincule um plano sugerido ou um plano avulso para começar a gestão operacional deste item."
            />
          ) : (
            <div className="space-y-4">
              {linkedPlans.map((plan) => (
                <OperationalPlanCard
                  key={plan.id}
                  itemId={itemId as string}
                  plan={plan}
                  responsibleUsers={responsibleUsers.map((user) => ({ id: user.id, name: user.name }))}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
